import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/shadcn/sheet';

export interface TambahPadelGamePlayerOption {
  id: number;
  name: string;
}

interface TambahPadelGameTeamsSectionProps {
  teams: number[][];
  participants: TambahPadelGamePlayerOption[];
  picker: { teamIndex: number; slotIndex: number } | null;
  onPickerChange: (picker: { teamIndex: number; slotIndex: number } | null) => void;
  onTeamsChange: (teams: number[][]) => void;
}

function emptyTeam(): number[] {
  return [0, 0];
}

function playerName(participants: TambahPadelGamePlayerOption[], id: number): string {
  return participants.find((p) => p.id === id)?.name || 'Pilih pemain';
}

export default function TambahPadelGameTeamsSection({
  teams,
  participants,
  picker,
  onPickerChange,
  onTeamsChange,
}: TambahPadelGameTeamsSectionProps) {
  const usedIds = new Set(teams.flat().filter((id) => id > 0));
  const available = participants.filter((p) => !usedIds.has(p.id));
  const ensureTeams = teams.length === 0 ? [emptyTeam(), emptyTeam()] : teams;

  const updateSlot = (teamIndex: number, slotIndex: number, participantId: number) => {
    const next = ensureTeams.map((team) => [...team]);
    while (next.length <= teamIndex) next.push(emptyTeam());
    next[teamIndex][slotIndex] = participantId;
    onTeamsChange(next);
    onPickerChange(null);
  };

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-4 sm:p-5 shadow-2xs">
      <h2 className="text-base font-extrabold text-[#0E1726]">Susunan Tim (Ganda)</h2>
      <p className="mt-1 text-xs text-gray-400">
        Tentukan pasangan tiap tim. Tim bertemu semua tim lain sekali.
      </p>

      <div className="mt-3 space-y-3">
        {ensureTeams.map((team, teamIndex) => (
          <Card key={`team-${teamIndex}`} className="rounded-2xl border-gray-100 p-3 shadow-none">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-extrabold text-[#0E1726]">Tim {teamIndex + 1}</p>
              {ensureTeams.length > 2 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Hapus tim ${teamIndex + 1}`}
                  className="h-8 w-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600"
                  onClick={() => onTeamsChange(ensureTeams.filter((_, i) => i !== teamIndex))}
                >
                  <Icon icon="mdi:trash-can-outline" className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="space-y-2">
              {[0, 1].map((slotIndex) => {
                const id = team[slotIndex] ?? 0;
                return (
                  <Button
                    key={`slot-${teamIndex}-${slotIndex}`}
                    type="button"
                    variant="secondary"
                    onClick={() => onPickerChange({ teamIndex, slotIndex })}
                    className="h-auto w-full justify-between rounded-xl bg-gray-50 px-3 py-3 text-sm font-semibold text-gray-700"
                  >
                    <span className={id ? 'text-[#0E1726]' : 'text-gray-400'}>
                      {id ? playerName(participants, id) : 'Pilih pemain'}
                    </span>
                    <Icon icon="mdi:magnify" className="h-4 w-4 text-gray-400" />
                  </Button>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        className="mt-3 h-auto w-full rounded-xl border-dashed border-gray-200 py-3 text-sm font-semibold text-[#FC480D]"
        onClick={() => onTeamsChange([...ensureTeams, emptyTeam()])}
      >
        + Tambah Tim
      </Button>

      <Sheet open={picker != null} onOpenChange={(open) => !open && onPickerChange(null)}>
        <SheetContent side="bottom" className="rounded-t-3xl">
          <SheetHeader>
            <SheetTitle>Pilih Pemain</SheetTitle>
            <SheetDescription>Hanya pemain yang belum masuk tim lain.</SheetDescription>
          </SheetHeader>
          {available.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center">
              <Icon icon="mdi:account-group-outline" className="h-10 w-10 text-gray-300" />
              <p className="mt-3 text-sm font-bold text-[#0E1726]">Semua pemain sudah masuk tim</p>
            </div>
          ) : (
            <div className="mt-4 max-h-80 space-y-2 overflow-y-auto">
              {available.map((player) => (
                <Button
                  key={player.id}
                  type="button"
                  variant="outline"
                  className="h-auto w-full justify-between rounded-xl border-gray-100 px-3 py-3"
                  onClick={() => picker && updateSlot(picker.teamIndex, picker.slotIndex, player.id)}
                >
                  <span className="text-sm font-bold text-[#0E1726]">{player.name}</span>
                  <Icon icon="mdi:plus-circle-outline" className="h-5 w-5 text-[#FC480D]" />
                </Button>
              ))}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
